import React, { useEffect, useState } from "react";
import {
    Box,
    Paper,
    TextField,
    Button,
    Typography,
    Alert,
    CircularProgress,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Logo from "../assets/logoazul.png";
import { validatePassword } from "../utils/passwordValidation";

function mapResetError(error) {
    const code = error?.code || "";

    if (code === "auth/expired-action-code") {
        return "Este link expirou. Solicite um novo e-mail de redefinição.";
    }
    if (code === "auth/invalid-action-code" || code === "auth/invalid-oob-code") {
        return "Link inválido ou já utilizado. Solicite um novo e-mail.";
    }
    if (code === "auth/weak-password") {
        return "Senha fraca. Use letras e números (mínimo 6 caracteres).";
    }

    return "Não foi possível redefinir a senha. Tente solicitar um novo link.";
}

export default function ResetPasswordPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { verifyResetCode, completePasswordReset } = useAuth();

    const oobCode = searchParams.get("oobCode") || "";
    const mode = searchParams.get("mode") || "";

    const [verifying, setVerifying] = useState(true);
    const [linkValid, setLinkValid] = useState(false);
    const [linkError, setLinkError] = useState("");
    const [accountEmail, setAccountEmail] = useState("");

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [formError, setFormError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;

        async function validateLink() {
            if (mode !== "resetPassword" || !oobCode) {
                if (!cancelled) {
                    setLinkValid(false);
                    setLinkError("Link incompleto ou inválido.");
                    setVerifying(false);
                }
                return;
            }

            const res = await verifyResetCode(oobCode);

            if (cancelled) return;

            if (res.ok) {
                setLinkValid(true);
                setAccountEmail(res.email || "");
            } else {
                setLinkValid(false);
                setLinkError(mapResetError(res.error));
            }

            setVerifying(false);
        }

        validateLink();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, oobCode]);

    async function handleSubmit(e) {
        e?.preventDefault();
        if (submitting || !linkValid) return;

        const passwordError = validatePassword(password);
        if (passwordError) {
            setFormError(passwordError);
            return;
        }

        if (password !== confirmPassword) {
            setFormError("A confirmação da senha não confere.");
            return;
        }

        setSubmitting(true);
        setFormError("");

        const res = await completePasswordReset(oobCode, password);

        setSubmitting(false);

        if (!res.ok) {
            setFormError(mapResetError(res.error));
            return;
        }

        navigate("/auth", {
            replace: true,
            state: { passwordResetSuccess: true },
        });
    }

    return (
        <Box
            sx={{
                width: "100%",
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "var(--backgroundPage)",
                p: 2,
            }}
        >
            <Paper
                elevation={6}
                sx={{
                    width: "100%",
                    maxWidth: 420,
                    p: 4,
                    borderRadius: "22px",
                    textAlign: "center",
                    background: "var(--branco)",
                }}
            >
                <img src={Logo} alt="logo" style={{ width: 150, marginBottom: 20 }} />

                {verifying ? (
                    <Box sx={{ py: 4 }}>
                        <CircularProgress size={36} />
                        <Typography sx={{ mt: 2, opacity: 0.7 }}>
                            Validando link...
                        </Typography>
                    </Box>
                ) : !linkValid ? (
                    <Box sx={{ textAlign: "left" }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                            Link indisponível
                        </Typography>
                        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                            {linkError}
                        </Alert>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => navigate("/auth", { state: { mode: "forgot" } })}
                            sx={{
                                py: 1.4,
                                borderRadius: "12px",
                                background: "var(--azul)",
                                ":hover": { background: "var(--azul)" },
                            }}
                        >
                            Solicitar novo link
                        </Button>
                        <Button
                            fullWidth
                            variant="text"
                            onClick={() => navigate("/auth")}
                            sx={{ mt: 1.5 }}
                        >
                            Voltar ao login
                        </Button>
                    </Box>
                ) : (
                    <Box component="form" onSubmit={handleSubmit} sx={{ textAlign: "left" }}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            Nova senha
                        </Typography>
                        <Typography sx={{ mt: 1, mb: 3, opacity: 0.6, fontSize: 14 }}>
                            {accountEmail
                                ? `Defina uma nova senha para ${accountEmail}.`
                                : "Defina uma nova senha para sua conta."}
                        </Typography>

                        {formError && (
                            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                                {formError}
                            </Alert>
                        )}

                        <TextField
                            fullWidth
                            type="password"
                            label="Nova senha"
                            margin="normal"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="new-password"
                            disabled={submitting}
                        />

                        <TextField
                            fullWidth
                            type="password"
                            label="Confirmar nova senha"
                            margin="normal"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            autoComplete="new-password"
                            disabled={submitting}
                        />

                        <Typography sx={{ fontSize: 12, opacity: 0.55, mt: 1 }}>
                            Mínimo 6 caracteres, com letras e números.
                        </Typography>

                        <Button
                            fullWidth
                            type="submit"
                            variant="contained"
                            disabled={submitting}
                            sx={{
                                mt: 3,
                                py: 1.4,
                                fontSize: 16,
                                borderRadius: "12px",
                                background: "var(--azul)",
                                ":hover": { background: "var(--azul)" },
                                opacity: submitting ? 0.7 : 1,
                            }}
                        >
                            {submitting ? "Salvando..." : "Redefinir senha"}
                        </Button>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
